import { useFormContext, useWatch } from 'react-hook-form'

import { useDigitalProductStore } from '@/features/store/hook'
import { canRedeemFromStore } from '@/features/store/lib/storeHours'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { Button } from '@/shared/ui/Button'
import { alert } from '@/shared/ui/dialog'
import { GameIcon } from '@/shared/ui/GameIcon'
import { useResourceContext } from '@/shared/ui/Resource'

import {
  CHECKIN_REQUIRED_REASON,
  showCheckinRequiredAlert
} from '../lib/checkinRequiredNotice'
import { isRedeemBlocked, itemPrice, redeemBlockMessage } from '../lib/redeemBlock'
import { isSoldOut } from '../lib/stock'
import { showStoreClosedAlert } from '../lib/storeClosedNotice'

export const DigitalProductStoreBottomSheetFooter = ({ children }) => {
  const { reward_items, isClaim, partner: rewardPartner } = useResourceContext()
  const { navigateTo } = useNavigation()
  const { formState } = useFormContext()
  const { isValid } = formState
  const quantity = useWatch({ name: 'quantity', defaultValue: 1 })
  const variation = useWatch({ name: 'reward_item_id', defaultValue: '' })
  const selectedVariation = reward_items.find(item => item.id === variation)
  /* O prazo de retirada saiu daqui em 2026-09-17, junto com a expiração
     automática: a reserva não vence mais sozinha, então "Retire até 18h00"
     prometeria uma perda que não acontece. O que continua valendo é o
     fechamento da lojinha para **reservar**, e disso quem fala é o botão. */
  const { storeStatus, openingHours } = useDigitalProductStore()

  /* Fora da janela a api recusa com `reward_shop_closed`. O botão diz isso em
     vez de aceitar o toque e devolver um erro — o Participante que escolheu a
     variação e a quantidade não deve descobrir no envio que a loja fechou.
     `storeStatus` revalida ao abrir o sheet (ver BottomSheet/index.jsx) —
     sem isso este gate travava com dado de horário obsoleto. */
  const isClosed = !canRedeemFromStore(storeStatus)

  // Critério é estoque, e não `redeemable`, que o serializer devolve nulo para
  // quem ainda não tem progresso no evento. A lista chega pelo mesmo poll de
  // 60s que alimenta a vitrine, então esgotar com o sheet aberto desabilita o
  // resgate sem o Participante tocar em nada.
  const isSelectedVariationSoldOut =
    !!selectedVariation && isSoldOut(selectedVariation)

  // A decisão é da API: o mesmo serviço avalia a listagem e o resgate, então o
  // botão nunca oferece o que a criação do pedido vai recusar.
  const redeemBlocked = isRedeemBlocked(selectedVariation)

  const isBlocked = !isValid || isClosed || redeemBlocked || isSelectedVariationSoldOut

  // Mesma prioridade de `EligibilityService#checkin_missing?` na api: parceiro
  // do reward primeiro, item só como reserva.
  const checkinPartner = rewardPartner || selectedVariation?.partner

  /* Travado sem tocar não avisa nada — só um botão apagado que ninguém lê no
     meio do evento. `aria-disabled` (não `disabled`) mantém o toque chegando
     aqui: o botão continua com a aparência de bloqueado (mesmo CSS de
     `disabled` via `[aria-disabled=true]`), mas o clique dispara o popup com
     o motivo em vez de simplesmente não fazer nada. */
  const handleReserveClick = event => {
    if (isClaim) {
      event.preventDefault()
      return
    }

    if (isClosed) {
      event.preventDefault()
      void showStoreClosedAlert(openingHours?.openAt)
      return
    }

    if (redeemBlocked) {
      event.preventDefault()

      if (selectedVariation?.redeem_blocked_reason === CHECKIN_REQUIRED_REASON) {
        void showCheckinRequiredAlert(checkinPartner, navigateTo)
        return
      }

      void alert({
        variant: 'warning',
        title: 'Resgate bloqueado',
        description: redeemBlockMessage(selectedVariation?.redeem_blocked_reason)
      })
      return
    }

    if (isSelectedVariationSoldOut) {
      event.preventDefault()
      void alert({
        variant: 'warning',
        title: 'Item esgotado',
        description: 'Esta variação esgotou. Escolha outra opção ou volte mais tarde.'
      })
      return
    }

    if (!isValid) {
      event.preventDefault()
      void alert({
        variant: 'warning',
        title: 'Escolha uma variação',
        description: 'Selecione uma opção e a quantidade antes de reservar.'
      })
    }
  }

  return (
    <div className="flex w-full flex-col gap-2">
      {redeemBlocked && (
        <p
          className="m-0 text-center text-xs font-semibold text-gray-600"
          role="status"
        >
          {redeemBlockMessage(selectedVariation?.redeem_blocked_reason)}
        </p>
      )}
      <div className="flex gap-4">
        {children}
        <Button
          variant="primary"
          type="submit"
          form="reward_request"
          aria-disabled={isBlocked || isClaim}
          onClick={handleReserveClick}
          showTrailingIcon={false}
          className="flex-grow [&>span]:flex [&>span]:w-full [&>span]:items-center [&>span]:justify-between"
        >
          <>
            <span className="text-base font-bold">
              {isClosed ? 'Lojinha fechada' : isClaim ? 'Reservando...' : 'Reservar'}
            </span>
            {!isClosed && (quantity || quantity == 0) && selectedVariation && (
              <span className="flex items-center justify-center">
                <GameIcon name="coins" size={20} className="mr-2" />
                <span className="text-lg font-bold">
                  {quantity * itemPrice(selectedVariation)}
                </span>
              </span>
            )}
          </>
        </Button>
      </div>
    </div>
  )
}
